import { useEffect, useState, type DependencyList } from 'react';
import { isAxiosError } from 'axios';

export type AuthGateState<T> =
  | { kind: 'loading' }
  | { kind: 'anon' }
  | { kind: 'error'; message: string }
  | { kind: 'authed'; data: T; isRefreshing: boolean };

/** Сообщение об отказе: своё у 403, серверное у остальных ответов. */
function errorMessage(err: unknown): string {
  if (isAxiosError<{ message?: string }>(err)) {
    if (err.response?.status === 403) {
      return 'У вас нет прав на этот раздел. Обратитесь к администратору, если он вам нужен.';
    }
    if (err.response?.data?.message) return err.response.data.message;
  }

  return 'Не удалось загрузить данные. Попробуйте обновить страницу.';
}

/**
 * Общий паттерн защищённых страниц: дёрнуть BFF-эндпоинт и разложить ответ на
 * три исхода — данные, «сессии нет» и «не получилось».
 *
 * На /login уводит ТОЛЬКО 401. Раньше туда же вела любая ошибка, и человек с
 * урезанной ролью, открыв недоступный ему раздел, получал 403 и выбрасывался
 * на страницу входа — выглядело так, будто его разлогинило, хотя сессия жива.
 *
 * По умолчанию фетчит один раз при монтировании (страницы без параметров вроде
 * дашборда). Если результат зависит от внешних параметров (например, номера
 * страницы в пагинации) – передать их в `deps`: при их изменении будет новый
 * запрос, но если данные уже когда-то загружались, состояние остаётся `authed`
 * со СТАРЫМИ данными и флагом `isRefreshing: true` (а не полноэкранным
 * loading) – так тулбар, поиск, пагинация не мигают на каждый перезапрос.
 */
export function useAuthGate<T>(
  fetcher: () => Promise<T>,
  deps: DependencyList = [],
): AuthGateState<T> {
  const [state, setState] = useState<AuthGateState<T>>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState((prev) =>
      prev.kind === 'authed' ? { ...prev, isRefreshing: true } : { kind: 'loading' },
    );
    fetcher()
      .then((data) => {
        if (!cancelled) setState({ kind: 'authed', data, isRefreshing: false });
      })
      .catch((err: unknown) => {
        if (cancelled) return;

        const unauthorized = isAxiosError(err) && err.response?.status === 401;
        setState(unauthorized ? { kind: 'anon' } : { kind: 'error', message: errorMessage(err) });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- зависимости задаёт вызывающий код через deps
  }, deps);

  return state;
}
